import RowItem from "components/shareable/RowItem";
import millify from "millify";
import Loader from "elements/Loader";
import ModalSymphony from "elements/ModalSymphony";
import useFacebookConnections from "Hooks/useFacebookConnections";
import { toastError } from "modules/Toast";
import { FBCustomAudience } from "modules/Types";
import { CSSProperties, useEffect, useState } from "react";
import ButtonSymphony from "elements/ButtonSymphony";
import { queryFB } from "helpers/FB";
import { makeKey } from "modules/Utils";

export default function ImportCustomAudiences({
    visible,
    onClose,
    onImport
}: {
    visible: boolean;
    onClose: () => void;
    onImport: (audiences: FBCustomAudience[]) => void;
}) {
    const {
        selectedFacebookAdAccount,
        loggedInFacebookUser
    } = useFacebookConnections();

    const [loadingCustomAudiences, setLoadingCustomAudiences] = useState(false);
    const [customAudiences, setCustomAudiences] = useState<FBCustomAudience[]>([]);
    const [selectedCustomAudiences, setSelectedCustomAudiences] = useState<FBCustomAudience[]>([]);

    useEffect(() => {
        if (visible) {
            getCustomAudiencesFromAdAccount();
        } else {
            // Reset selections when modal closes
            setSelectedCustomAudiences([]);
        }
    }, [visible]);

    /** 
     * Fetches all custom audiences from the Facebook Ad Account
     * Handles pagination with recursion to get all audiences
     */
    const getAllCustomAudiences = async (fbAdAccount: {
        id: string
    }, after: string | null = null): Promise<FBCustomAudience[]> => {
        const params = {
            fields: 'name,id,rule,rule_aggregation,description,delivery_status,lookalike_spec,data_source,subtype,approximate_count_lower_bound,approximate_count_upper_bound,is_value_based,opt_out_link',
            filtering: [
                {
                    field: 'subtype',
                    operator: 'NOT_EQUAL',
                    value: 'LOOKALIKE'
                }
            ],
            limit: 200,
            after,
            access_token: loggedInFacebookUser?.access_token,
        };

        const response = await queryFB(`/${fbAdAccount.id}/customaudiences`, { params });
        if (response.data && response.paging && response.paging.cursors && response.paging.cursors.after) {
            const returned = await getAllCustomAudiences(fbAdAccount, response.paging.cursors.after);
            if (!returned) return response.data;
            return response.data.concat(returned);
        } else {
            return response.data;
        }
    };

    const getCustomAudiencesFromAdAccount = async () => {
        try {
            setLoadingCustomAudiences(true);
            if (selectedFacebookAdAccount && selectedFacebookAdAccount.id) {
                const audiences: FBCustomAudience[] = await getAllCustomAudiences(selectedFacebookAdAccount);
                setCustomAudiences(audiences);
            } else {
                toastError('No Facebook Ad Account selected');
            }
        } catch (error) {
            console.error('Error loading custom audiences:', error);
            toastError('Failed to load custom audiences');
        } finally {
            setLoadingCustomAudiences(false);
        }
    };

    const toggleAudienceSelection = (audience: FBCustomAudience) => {
        const isSelected = selectedCustomAudiences.some(a => a.id === audience.id);

        if (isSelected) {
            setSelectedCustomAudiences(selectedCustomAudiences.filter(a => a.id !== audience.id));
        } else {
            setSelectedCustomAudiences([...selectedCustomAudiences, audience]);
        }
    };

    const importSelectedAudiences = () => {
        onImport(selectedCustomAudiences);
        onClose();
    };

    return (
        <ModalSymphony
            className="importAudiencesFromAdAccountModal"
            isOpen={visible}
            title="Import Audiences from your Ad Account"
            onClose={onClose}
        >
            <div className="importAudiencesContainer" style={sty.importAudiencesContainer}>
                {loadingCustomAudiences ? (
                    <div className="flex-col flex items-center m-8 mx-auto">
                        <Loader title="Loading Custom Audiences..." />
                    </div>
                ) : customAudiences.length > 0 ? (
                    <>
                        <div className="importAudiencesHeader" style={sty.importAudiencesHeader}>
                            <p>Select the custom audiences you want to import for targeting.</p>
                            <p className="text-sm text-gray-500">
                                {selectedCustomAudiences.length} of {customAudiences.length} audiences selected
                            </p>
                        </div>

                        <div className="audiencesList" style={sty.audiencesList}>
                            {customAudiences.map((audience, index) => {
                                const isSelected = selectedCustomAudiences.some(a => a.id === audience.id);
                                let sizeLabel = '';

                                if (audience.approximate_count_lower_bound > 0 || audience.approximate_count_upper_bound > 0) {
                                    if (audience.approximate_count_lower_bound === audience.approximate_count_upper_bound) {
                                        sizeLabel = `Approx. ${millify(audience.approximate_count_lower_bound)} people`;
                                    } else {
                                        sizeLabel = `Approx. ${millify(audience.approximate_count_lower_bound)} - ${millify(audience.approximate_count_upper_bound)} people`;
                                    }
                                }

                                return (
                                    <RowItem
                                        key={makeKey(audience.id, index)}
                                        multiSelect
                                        selected={isSelected}
                                        onClick={() => toggleAudienceSelection(audience)}
                                        name={audience.name}
                                        secondRowContent={sizeLabel ? (
                                            <div className="mt-1">
                                                <p className="text-xs">{sizeLabel}</p>
                                            </div>
                                        ) : null}
                                    />
                                );
                            })}
                        </div>

                        <div className="importButtonContainer" style={sty.importButtonContainer}>
                            <ButtonSymphony
                                className="importAudiencesButton"
                                disabled={selectedCustomAudiences.length === 0}
                                onClick={importSelectedAudiences}
                            >
                                Import {selectedCustomAudiences.length} Audiences
                            </ButtonSymphony>
                        </div>
                    </>
                ) : (
                    <div className="noAudiencesMessage" style={sty.noAudiencesMessage}>
                        <p>No custom audiences found in your Facebook Ad Account.</p>
                        <p className="text-sm text-gray-500 mt-2">
                            Custom audiences need to be created in your Facebook Ads Manager before they can be imported.
                        </p>
                    </div>
                )}
            </div>
        </ModalSymphony>
    );
}

const sty: Record<string, CSSProperties> = {
    importAudiencesContainer: {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    },
    importAudiencesHeader: {
        marginBottom: 16
    },
    audiencesList: {
        maxHeight: 400,
        overflowY: 'auto',
        border: '1px solid #e0e0e0',
        borderRadius: 4,
        marginBottom: 16
    },
    importButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end'
    },
    noAudiencesMessage: {
        textAlign: 'center',
        padding: 16,
    }
};
